import React, { useState } from 'react';
import { extractUserAlias, getUserProfilePictureUrl, getUserInitials } from '../../utils/userUtils';

interface UserAvatarProps {
  username?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ username, size = 'md', className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  if (!username) {
    return (
      <div
        className={`relative rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 text-white flex items-center justify-center font-semibold shadow-md ring-2 ring-white/20 ${getSizeClasses(size)} ${className}`}
      >
        <span className="drop-shadow-sm">U</span>
      </div>
    );
  }

  const alias = extractUserAlias(username);
  const initials = getUserInitials(username);
  const profilePictureUrl = alias ? getUserProfilePictureUrl(alias) : null;

  // If no alias could be extracted or image failed to load, show initials
  if (!alias || imageError) {
    return (
      <div
        className={`relative rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center font-semibold shadow-md ring-2 ring-white/20 hover:ring-white/30 transition-all duration-200 ${getSizeClasses(size)} ${className}`}
      >
        <span className="drop-shadow-sm">{initials}</span>
        {/* Subtle inner glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent to-white/10"></div>
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-full overflow-hidden shadow-md ring-2 ring-white/20 hover:ring-white/30 hover:shadow-lg transition-all duration-300 ease-out ${getSizeClasses(size)} ${className}`}
    >
      {/* Loading placeholder with shimmer effect */}
      {imageLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-200 to-secondary-300 rounded-full flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer -skew-x-12"></div>
          <div className="text-secondary-500 font-semibold drop-shadow-sm relative z-10">{initials}</div>
        </div>
      )}

      {/* Profile picture with enhanced styling */}
      <img
        src={profilePictureUrl || ''}
        alt={`${username}'s profile`}
        className={`w-full h-full object-cover transition-all duration-300 hover:scale-105 ${
          imageLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
      />

      {/* Subtle overlay for depth */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-t from-black/5 to-transparent pointer-events-none"></div>

      {/* Online status indicator */}
      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full shadow-sm"></div>
    </div>
  );
};

function getSizeClasses(size: 'sm' | 'md' | 'lg'): string {
  switch (size) {
    case 'sm':
      return 'w-6 h-6 text-xs';
    case 'md':
      return 'w-8 h-8 text-sm';
    case 'lg':
      return 'w-10 h-10 text-base';
    default:
      return 'w-8 h-8 text-sm';
  }
}

export default UserAvatar;
